import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import store from '@/store';

dayjs.extend(utc);
dayjs.extend(advancedFormat);

// Number of milliseconds in a second
const millisecondsInSecond = 1000;
const defaultDayEndTime = '23:59:59';
const yearsForDecade = 9;
const monthsForYear = 11;
const daysForWeeks = 6;
const dayEndHours = 23;
const dayEndMinutes = 59;
const dayEndSeconds = 59;

// Default month names available
const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

/**
 * Take timestamp as input
 * Resolve it to full month name
 * @param {string} timestamp
 */
const getMonth = timestamp => {
  return monthNames[dayjs(timestamp).month()];
};

/**
 * Converts a Unix timestamp to a Date object or Unix timestamp based on the specified time zone.
 * @param {object} options - The options object.
 * @param {number} options.timestamp - The Unix timestamp to convert.
 * @param {string} [options.timeZone='local'] - The time zone to use for the conversion. Possible values: 'local' (default), 'utc'.
 * @returns {object|number} - The Date object representing the converted timestamp or the Unix timestamp, depending on the time zone.
 */
const computeUnixTimestamp = ({ timestamp, timeZone = 'local' } = {}) => {
  if (timeZone === 'utc') {
    // returns a Unix timestamp in UTC
    return dayjs.utc(timestamp).unix();
  } else {
    // Convert Unix timestamp to Date object in local time zone
    return dayjs.unix(timestamp / millisecondsInSecond);
  }
};

/**
 * Converts a Unix timestamp to an ISO string representation
 * @param {Object} options - The options object.
 * @param {number} options.timestamp - The Unix timestamp to convert.
 * @returns {string} - The ISO string representation of the converted timestamp.
 */
const convertToUnixISOString = ({ timestamp } = {}) => {
  // Create a date object representing the converted timestamp
  const dateObj = computeUnixTimestamp({ timestamp });

  // return the ISO string representation of the date object
  return dateObj.toISOString();
};

/**
 * Get a formatted date string based on the provided parameters.
 *
 * @param {Object} options - The options object.
 * @param {Date|number|string} options.date - The date to format.
 * @param {string} [options.format='YYYY-MM-DD'] - The desired format of the date string.
 * @param {string} [options.timeZone='local'] - The time zone to use for formatting ('local' or 'utc').
 * @param {boolean} [options.computeUnix=false] - Whether to compute a Unix timestamp from the date and format it. Default is false.
 * @returns {string} The formatted date string.
 */
const getFormattedDate = ({
  date,
  format = 'YYYY-MM-DD',
  timeZone = 'local',
  computeUnix = false
} = {}) => {
  if (computeUnix) {
    // Compute Unix timestamp and format it
    return computeUnixTimestamp({ timestamp: date }).format(format);
  }
  // Check if the desired time zone is UTC
  else if (timeZone === 'utc') {
    // Format the date in UTC mode
    return dayjs.utc(date).format(format);
  } else {
    // Format the date in the local time zone
    return dayjs(date).format(format);
  }
};

/**
 * Gets the Unix timestamp in milliseconds for the specified date
 * @param {Object} options - The options object.
 * @param {string|Date} options.date - The date to retrieve the Unix timestamp for.
 * @returns {string} - The Unix timestamp in milliseconds as a string.
 */
const getUnixTimestamp = ({ date } = {}) =>
  getFormattedDate({ date, format: 'x' });

/**
 * Modifies the given date based on the specified operation, amount, and unit.
 * Returns the formatted date or ISO string representation.
 *
 * @param {Object} options - The options object containing the parameters.
 * @param {string} options.operation - The operation to perform: 'add' or 'subtract'.
 * @param {Date|string} options.date - The date to modify.
 * @param {number} options.amount - The amount to add or subtract from the date.
 * @param {string} options.unit - The unit of time for the modification: 'year', 'month', 'day', etc.
 * @param {string} [options.format] - Optional date format to return the modified date in.
 * @param {boolean} [options.getDateObj] - Optional to return the modified date Object.
 * @returns {string} The formatted date or ISO string representation or date object.
 */
const modifyDate = ({
  date,
  operation,
  amount,
  unit,
  format,
  getDateObj = false
} = {}) => {
  const dateObj = dayjs(date);
  let modifiedDateObj = dateObj;

  if (operation) {
    switch (operation) {
      case 'add':
        modifiedDateObj = dateObj.add(amount, unit);
        break;

      case 'subtract':
        modifiedDateObj = dateObj.subtract(amount, unit);
        break;
    }
  }

  if (getDateObj) {
    return modifiedDateObj;
  }

  return format
    ? modifiedDateObj.format(format)
    : modifiedDateObj.toISOString();
};

/**
 * Calculates the difference between two dates in the specified unit
 * @param {string|Date} date1 - The first date to compare.
 * @param {string|Date} date2 - The second date to compare.
 * @param {string} unit - The unit of the difference to calculate (e.g., 'years', 'months', 'weeks', 'days', 'hours', 'minutes', 'seconds').
 * @returns {number} - The difference between the two dates in the specified unit.
 */
const calculateDateDifference = ({
  endDate,
  startDate,
  unit = 'days'
} = {}) => {
  // Create Date objects for the two dates
  const endDateObj = dayjs(endDate);
  const startDateObj = dayjs(startDate);

  // Calculate the difference between the two dates in the specified unit
  return endDateObj.diff(startDateObj, unit);
};

const computeUTCTimestamp = selectedDate => {
  let finalDate = '';
  selectedDate = dayjs.utc(selectedDate).format('YYYY-MM-DD');
  finalDate = `${selectedDate} ${defaultDayEndTime}`;
  return dayjs.utc(finalDate).unix() * millisecondsInSecond;
};

/**
 *
 * @param {Date} theDate
 * @param {number} h
 * @param {number} m
 * @param {number} s
 */
const setTimeForDate = (theDate, h, m, s) => {
  return dayjs
    .utc(theDate)
    .set('hour', h)
    .set('minute', m)
    .set('second', s)
    .format('x');
};

const setDateRange = (timeUnit, selectedDate) => {
  let dateRangeFrom = '';
  let dateRangeTo = '';
  switch (timeUnit) {
    case 'DECADE':
      dateRangeFrom = dayjs
        .utc(selectedDate)
        .subtract(yearsForDecade, 'years')
        .format('YYYY');
      dateRangeTo = dayjs.utc(selectedDate).format('YYYY');
      break;

    case 'YEAR':
      dateRangeFrom = dayjs
        .utc(selectedDate)
        .subtract(monthsForYear, 'months')
        .format('MMMM YYYY');
      dateRangeTo = dayjs.utc(selectedDate).format('MMMM YYYY');
      break;

    case 'MONTH':
      dateRangeFrom = dayjs
        .utc(selectedDate)
        .subtract(1, 'month')
        .add(1, 'day')
        .format('DD MMM');
      dateRangeTo = dayjs.utc(selectedDate).format('DD MMM');
      break;

    case 'WEEK':
      dateRangeFrom = dayjs
        .utc(selectedDate)
        .subtract(daysForWeeks, 'days')
        .format('DD MMM');
      dateRangeTo = dayjs.utc(selectedDate).format('DD MMM');
      break;

    default:
      //'DAY'
      dateRangeFrom = dayjs
        .utc(selectedDate)
        .subtract(1, 'day')
        .format('DD MMM');
      dateRangeTo = dayjs.utc(selectedDate).format('DD MMM');
      break;
  }
  return [dateRangeFrom, dateRangeTo];
};

/**
 * Converts the given userDate to a formatted date string in the format 'DD/MM/YYYY'.
 * @param {string} userDate - The date to be formatted.
 * @returns {string} The formatted date string.
 */
const dateMonthYearDate = userDate => {
  return dayjs(userDate).format('DD/MM/YYYY');
};

/**
 * Description: Converts a user-provided date to the format 'YYYY-MM-DD'
 * @param {string} userDate - The date to be formatted.
 * @returns {string} The formatted date string in 'YYYY-MM-DD' format
 */
const YearMonthdateDate = userDate => dayjs.utc(userDate).format('YYYY-MM-DD');

// Function to get the maximum date in the format 'YYYY-MM-DD'
const getMaxDate = () => {
  const todayDate = new Date().toISOString().substr(0, 10);
  const maxDate = dayjs.utc(todayDate).format('YYYY-MM-DD');
  return maxDate;
};

/**
 * Applies the specified format to a given timestamp.
 *
 * @param {string} timestamp - The timestamp to format.
 * @param {string} format - The format to apply to the timestamp.
 * @returns {string} - The formatted timestamp.
 */
function applyFormatToDateTime(timestamp, format) {
  const orgTimezone = store.state.timezone?.source || 'GMT';
  // Splitting the orgTimezone by 'GMT' to extract the UTC offset
  const UTCOffset = orgTimezone.split('GMT');

  // If a format is provided, format the timestamp
  if (format) {
    return dayjs
      .utc(timestamp)
      .utcOffset(UTCOffset[1])
      .format(format);
  }

  // If no format is provided, return the timestamp with the UTC offset
  return dayjs.utc(timestamp).utcOffset(UTCOffset[1]);
}

function getSelectedDates({ selectedDate, selectedRange }) {
  if (selectedRange === 0) {
    return {
      start: '',
      end: ''
    };
  }
  const theDate = String(selectedDate).split('T')[0];
  const orgTimezone = store.state.timezone?.source || 'GMT';
  const UTCOffset = orgTimezone.split('GMT');
  const timezoneFormattedStart = dayjs(applyFormatToDateTime(theDate)).subtract(
    selectedRange,
    'days'
  );

  let timezoneFormattedEnd = dayjs
    .utc(theDate)
    .utcOffset(UTCOffset[1])
    .set('hour', dayEndHours)
    .set('minute', dayEndMinutes)
    .set('second', dayEndSeconds)
    .add(selectedRange, 'days');

  const start = timezoneFormattedStart.format('x');

  let end = timezoneFormattedEnd.format('x');

  const today = dayjs
    .utc()
    .utcOffset(UTCOffset[1])
    .format('x');

  end = end > today ? today : end;
  return {
    start,
    end
  };
}

/**
 * Formats a timestamp as per the specified timezone.
 * @param {number} timestamp - The timestamp to be formatted.
 * @returns {string} The formatted date and time string.
 * Output: "29 Jun 2023 @ 10:30:00 PM"
 */
function getFormatedDateAsPerTimeZone(timestamp) {
  // Call the `applyFormatToDateTime` function with the specified timestamp and format.
  // The format 'DD MMM YYYY @ h:mm:ss A' represents the desired date and time format.
  return applyFormatToDateTime(timestamp, 'DD MMM YYYY @ h:mm:ss A');
}
/**
 * Returns a formatted date string based on the given timestamp.
 * @param {number} timestamp - The timestamp to format.
 * @returns {string} The formatted date string.
 * Output: "29/06/2023 @ 10:30:45 AM"
 */
function getFormatedDateAsPerTimeAndDate(timestamp) {
  return applyFormatToDateTime(timestamp, 'DD/MM/YYYY @ h:mm:ss A');
}

/**
 * Format the given timestamp to a specific date and time format.
 * @param {number} timestamp - The timestamp to format.
 * @returns {string} The formatted date and time string.
 * Output:- 29/06/2023 @ 10:30 PM
 */
function getFormatedDateAndTime(timestamp) {
  return applyFormatToDateTime(timestamp, 'DD/MM/YYYY @ hh:mm A');
}

/**
 * Returns the date and month in the specified format.
 * @param {number} timestamp - The timestamp representing the date.
 * @returns {string} - The formatted date and month.
 * Output: "29 Jun"
 */
function getDateAndMonth(timestamp) {
  return applyFormatToDateTime(timestamp, 'DD MMM');
}

/**
 * Returns a formatted date string with month, day, and year.
 * @param {number} timestamp - The timestamp to format.
 * @returns {string} The formatted date string.
 * Output: "Jun 30,2021"
 */
function getDateAndMonthAndYear(timestamp) {
  return applyFormatToDateTime(timestamp, 'MMM DD,YYYY');
}

/**
 * Returns the hour and minute from a given timestamp formatted as 'h:mm A'
 * @param {number} timestamp - The timestamp to extract hour and minute from
 * @returns {string} - The formatted hour and minute
 * Output: 9:00 AM
 */
function getHourAndMinute(timestamp) {
  return applyFormatToDateTime(timestamp, 'h:mm A');
}

// This function takes a timestamp as input and returns the corresponding UTC date as per the provided timestamp.
// Output:- Date obj
function getUTCDateAsPerTimeAndDate(timestamp) {
  return dayjs.utc(timestamp);
}

// All date constants
const dateConstants = {
  monthNames,
  dayEndHours,
  daysForWeeks,
  monthsForYear,
  dayEndMinutes,
  dayEndSeconds,
  yearsForDecade,
  defaultDayEndTime,
  millisecondsInSecond
};

// All date helper methods
const dateMethods = {
  getMonth,
  modifyDate,
  getMaxDate,
  setDateRange,
  setTimeForDate,
  getDateAndMonth,
  getFormattedDate,
  getHourAndMinute,
  getUnixTimestamp,
  getSelectedDates,
  dateMonthYearDate,
  YearMonthdateDate,
  computeUTCTimestamp,
  computeUnixTimestamp,
  applyFormatToDateTime,
  getFormatedDateAndTime,
  getDateAndMonthAndYear,
  convertToUnixISOString,
  calculateDateDifference,
  getUTCDateAsPerTimeAndDate,
  getFormatedDateAsPerTimeZone,
  getFormatedDateAsPerTimeAndDate
};
export { dateConstants, dateMethods };
